// El Alpha CMS: gestion (admin) y contenido publicado (alumno).
//
// Vivia en `lib/api.js` junto con otros veintidos apartados (R4.5). Quien lo importa
// no se entero: `@/lib/api` sigue exportando exactamente lo mismo.
import { api, BACKEND_URL } from "./client";

// ---- Alpha CMS (admin) ----

export const cmsListCourses = async () => (await api.get("/cms/courses")).data;
export const cmsCreateCourse = async (payload) => (await api.post("/cms/courses", payload)).data;
export const cmsGetCourse = async (id) => (await api.get(`/cms/courses/${id}`)).data;
export const cmsUpdateCourse = async (id, payload) => (await api.patch(`/cms/courses/${id}`, payload)).data;
export const cmsDeleteCourse = async (id) => (await api.delete(`/cms/courses/${id}`)).data;
export const cmsCreateLesson = async (courseId, payload) => (await api.post(`/cms/courses/${courseId}/lessons`, payload)).data;
export const cmsUpdateLesson = async (id, payload) => (await api.patch(`/cms/lessons/${id}`, payload)).data;
export const cmsDeleteLesson = async (id) => (await api.delete(`/cms/lessons/${id}`)).data;
export const cmsReorderLessons = async (courseId, orderedIds) =>
  (await api.post(`/cms/courses/${courseId}/reorder`, { ordered_ids: orderedIds })).data;
export const cmsAiDraft = async (lessonId, brief) =>
  (await api.post(`/cms/lessons/${lessonId}/ai-draft`, { brief })).data;
export const cmsGenerateActivities = async (lessonId, count = 4) =>
  (await api.post(`/cms/lessons/${lessonId}/activities/generate`, { count })).data;

// Compositor de lección: varios archivos (PDF/txt/md) → bloques organizados
// (texto verbatim + actividades intercaladas) listos para revisar y guardar.
export const cmsComposeLesson = async (lessonId, files) => {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  return (await api.post(`/cms/lessons/${lessonId}/compose`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  })).data;
};

// Reflexión formativa: Sócrates lee una respuesta abierta y devuelve badge + línea.
export const reviewReflexion = async ({ question, answer, competencia, guidance }) =>
  (await api.post("/reflexion/review", { question, answer, competencia, guidance: guidance || undefined })).data;
export const cmsImportAnalyze = async (files) => {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  const r = await api.post("/cms/import/analyze", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return r.data;
};
export const cmsImportApply = async (payload) =>
  (await api.post("/cms/import/apply", payload, { timeout: 300000 })).data;

// `privado: true` para lo que lleva candado —los recursos de la Biblioteca—. Un fichero
// privado NO sale por /api/files: lo sirve su modulo, que es el unico que sabe quien puede
// verlo. El defecto es publico porque es lo que son casi todos (imagenes de leccion).
export const cmsUpload = async (file, { privado = false } = {}) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/cms/upload${privado ? "?privado=true" : ""}`, form,
    { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};


// ---- Contenido publicado (alumno) ----

export const getPublicContent = async (competencia) => (await api.get(`/content/${competencia}`)).data;

// Los titulos de los cursos publicados de cada materia: `{materia: [titulo, ...]}`. Pesa
// unas pocas lineas de JSON, al contrario que `getPublicContent`, que trae cada curso con
// TODAS sus tarjetas de leccion -seiscientas setenta y siete hoy-. La portada pinta con esto
// las cinco tarjetas de los Agentes Alpha, y no puede permitirse lo otro.
/**
 * EL CATALOGO PUBLICADO, Y SE PIDE UNA VEZ PARA TODOS.
 *
 * QUE PASABA. Lo piden tres sitios —la portada en «Para tu curso», `/cursos` entera y el
 * panel del profesor—, y cada uno lo pedia por su cuenta en cada visita. Gabri, 20 de octubre
 * de 2026: *«en cursos tarda unos segundos adicionales cargar los cursos/etapa, siempre
 * sucede, se ve raro»*. Lo primero que se midio fue el servidor, y el servidor no era: 41 ms
 * en frio y 4 en caliente, cinco kilobytes. **Medir antes de optimizar es lo que evita
 * arreglar lo que no estaba roto.**
 *
 * LO QUE SI ARREGLA ESTO es el camino normal: quien llega por la portada ya tiene el catalogo
 * bajado cuando pulsa «Explora los cursos», asi que esa pantalla pinta con datos en el primer
 * fotograma en vez de pintarse vacia y rellenarse. Y de paso, dos piezas de la misma pantalla
 * que lo pidan a la vez hacen UNA peticion: se comparte la promesa, no el resultado, asi que
 * el segundo que llega se engancha a la que ya va en camino.
 *
 * SESENTA SEGUNDOS, Y NO PARA SIEMPRE. Es un catalogo, no una verdad eterna: quien acaba de
 * publicar un curso en el CMS tiene que verlo sin reiniciar nada. Un minuto cubre de sobra el
 * paseo entre pantallas, que es lo que esto viene a arreglar, y no esconde una publicacion
 * mas de un minuto. Con `{ recargar: true }` se pide fresco.
 *
 * UN FALLO NO SE GUARDA. Si la red se cae, la promesa rota se tira: el siguiente que pregunte
 * vuelve a intentarlo. Cachear un error es convertir un corte de wifi de un segundo en un
 * minuto de pantalla vacia.
 */
const VIDA_DEL_CATALOGO = 60000;
let catalogoEnCurso = null;

export const getCursosPublicados = async ({ recargar = false } = {}) => {
  const ahora = Date.now();
  if (!recargar && catalogoEnCurso && ahora - catalogoEnCurso.cuando < VIDA_DEL_CATALOGO) {
    return catalogoEnCurso.promesa;
  }
  const promesa = api.get("/cursos-publicados").then((r) => r.data);
  catalogoEnCurso = { cuando: ahora, promesa };
  promesa.catch(() => {
    if (catalogoEnCurso && catalogoEnCurso.promesa === promesa) catalogoEnCurso = null;
  });
  return promesa;
};
export const getPublicLesson = async (lessonId) => (await api.get(`/content/lessons/${lessonId}`)).data;
export const fileUrl = (url) => (url && url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);
// Los bytes de un recurso de la Biblioteca. Vuelve a comprobar el desbloqueo al servir:
// saber la direccion ya no basta, que era el agujero.
export const resourceFileUrl = (resourceId) =>
  fileUrl(`/api/resources/${resourceId}/archivo`);

export const submitContact = async (payload) => {
  const { data } = await api.post("/contact", payload);
  return data;
};

// LAS CIFRAS QUE LA PORTADA AFIRMA, contadas y no escritas. Antes salían de
// `/test/stats`, donde estaban puestas a mano: «+3500 estudiantes» y «98% de satisfacción»,
// ninguna de las dos cierta. Aquí vienen dos `count_documents` sobre lo publicado.
export const getCifras = async () => {
  const { data } = await api.get("/cifras");
  return data;
};
